import { useState, useEffect, useMemo } from 'react'
import { Tag, Trash2, Eye, EyeOff, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../i18n/apiError'
import { useMineMerker } from '../hooks/useMineMerker'
import { userAPI, type AvailableMerke } from '../services/api'
import LoadingSpinner from './LoadingSpinner'
import ErrorMessage from './ErrorMessage'

interface Props {
  onToast: (msg: string, type: 'success' | 'error') => void
}

export default function MineMerkerSection({ onToast }: Props) {
  const { t } = useTranslation('components')
  const { items, loading, error, refresh } = useMineMerker()

  const [saving, setSaving] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [availableMerker, setAvailableMerker] = useState<AvailableMerke[]>([])
  const [loadingMerker, setLoadingMerker] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)

  const atLimit = items.length >= 5

  // Fetch available brands when search is focused / query changes
  useEffect(() => {
    if (atLimit) return
    const load = async () => {
      try {
        setLoadingMerker(true)
        const res = await userAPI.getAvailableMerker()
        setAvailableMerker(res.data?.items ?? [])
      } catch {
        // Silently fail — user can retry
      } finally {
        setLoadingMerker(false)
      }
    }
    load()
  }, [items.length, atLimit])

  // Filter available brands by search query, exclude already-saved
  const savedCodes = useMemo(() => new Set(items.map(m => m.merke)), [items])
  const filteredMerker = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return availableMerker
      .filter(m => !savedCodes.has(m.merke))
      .filter(m => !q || m.merke_navn.toLowerCase().includes(q))
      .slice(0, 20)
  }, [availableMerker, savedCodes, searchQuery])

  const handleAdd = async (merke: AvailableMerke) => {
    try {
      setSaving(true)
      await userAPI.createMineMerke(merke.merke, merke.merke_navn)
      onToast(t('mineMerker.added', { name: merke.merke_navn }), 'success')
      setSearchQuery('')
      setShowDropdown(false)
      await refresh()
    } catch (err: any) {
      onToast(localizeApiDetail(err?.response?.data?.detail) || t('mineMerker.addError'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (id: number, currentActive: boolean) => {
    try {
      setSaving(true)
      await userAPI.toggleMineMerke(id, !currentActive)
      onToast(!currentActive ? t('mineMerker.activated') : t('mineMerker.deactivated'), 'success')
      await refresh()
    } catch (err: any) {
      onToast(localizeApiDetail(err?.response?.data?.detail) || t('mineMerker.updateError'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(t('mineMerker.confirmDelete', { name }))) return
    try {
      setSaving(true)
      await userAPI.deleteMineMerke(id)
      onToast(t('mineMerker.removed', { name }), 'success')
      await refresh()
    } catch (err: any) {
      onToast(localizeApiDetail(err?.response?.data?.detail) || t('mineMerker.deleteError'), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card p-6" data-testid="mine-merker-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <Tag className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('mineMerker.title')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('mineMerker.subtitle')}
            </p>
          </div>
        </div>
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
          {t('mineMerker.countOfMax', { n: items.length })}
        </span>
      </div>

      {loading ? <LoadingSpinner /> : error ? <ErrorMessage message={error} /> : (
        <div className="space-y-3">
          {/* Search dropdown — hidden when at limit */}
          {!atLimit && (
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setShowDropdown(true) }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder={t('mineMerker.searchPlaceholder')}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-dark-border rounded-sm bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100"
                  data-testid="merke-search-input"
                />
              </div>
              {showDropdown && (
                <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto border border-gray-200 dark:border-dark-border rounded-lg bg-white dark:bg-dark-card shadow-lg">
                  {loadingMerker ? (
                    <div className="p-3 text-sm text-gray-500 dark:text-gray-400">{t('mineMerker.loading')}</div>
                  ) : filteredMerker.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 dark:text-gray-400">{t('mineMerker.noneFound')}</div>
                  ) : (
                    filteredMerker.map(m => (
                      <button
                        key={m.merke}
                        onClick={() => handleAdd(m)}
                        disabled={saving}
                        className="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-dark-border transition-colors disabled:opacity-50"
                        data-testid={`merke-option-${m.merke}`}
                      >
                        {m.merke_navn}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Saved brands list */}
          {items.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('mineMerker.empty')}
            </p>
          )}

          {items.map(it => (
            <div
              key={it.id}
              className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                it.is_active
                  ? 'border-primary-300 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/10'
                  : 'border-gray-200 dark:border-dark-border opacity-50'
              }`}
              data-testid={`merke-row-${it.merke}`}
            >
              <div className="flex-1">
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {it.merke_navn}
                </span>
              </div>
              <button
                onClick={() => handleToggle(it.id, it.is_active)}
                disabled={saving}
                className="btn-secondary"
                title={it.is_active ? t('mineMerker.deactivate') : t('mineMerker.activate')}
                data-testid={`merke-toggle-${it.merke}`}
              >
                {it.is_active
                  ? <Eye className="w-4 h-4" />
                  : <EyeOff className="w-4 h-4" />
                }
              </button>
              <button
                onClick={() => handleDelete(it.id, it.merke_navn)}
                disabled={saving}
                className="btn-secondary text-red-600 dark:text-red-400"
                title={t('mineMerker.delete')}
                data-testid={`merke-delete-${it.merke}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
